import { useState, useEffect } from 'react';
import { Search, User, Building2, Phone, Mail, Calendar, Plus } from 'lucide-react';
import TenantManagementModal from '../../components/admin/TenantManagementModal';
import AddTenantModal from '../../components/admin/AddTenantModal';
import { apiService } from '../../services/api';

interface Tenant {
  _id: string;
  name: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  currentShop?: {
    _id: string;
    shopNumber: string;
    floor: string;
    size: number;
  };
  rentalHistory: Array<{
    shop: {
      _id: string;
      shopNumber: string;
      floor: string;
    };
    startDate: string;
    endDate?: string;
    status: string;
    monthlyRent: number;
    reasonForLeaving?: string;
  }>;
  status: string;
  createdAt: string;
}

export default function AdminTenants() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.getTenants();
      
      if (response.status === 'success' && response.data) {
        setTenants(response.data.tenants);
        setFilteredTenants(response.data.tenants);
      } else {
        setError('Failed to load tenants');
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
      setError('Failed to load tenants. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = tenants.filter(tenant => {
      const matchesSearch = 
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = !statusFilter || tenant.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    setFilteredTenants(filtered);
  }, [tenants, searchTerm, statusFilter]);

  const handleTenantUpdate = (updatedTenant: Tenant | any) => {
    setTenants(prev => 
      prev.map(tenant => 
        tenant._id === updatedTenant._id ? { ...tenant, ...updatedTenant } : tenant
      )
    );
    setSelectedTenant(updatedTenant as Tenant);
  };

  const handleTenantCreated = () => {
    loadTenants(); // Reload to ensure we have the latest data
  };

  const openTenantModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowTenantModal(true);
  };

  const closeTenantModal = () => {
    setShowTenantModal(false);
    setSelectedTenant(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600">Loading tenants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Tenants</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadTenants}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-6 lg:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
              Tenant Management
            </h1>
            <p className="text-base sm:text-lg text-gray-600">
              Manage tenant information, transfers, and rental history
            </p>
          </div>
          <button
            onClick={() => setShowAddTenantModal(true)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg hover:bg-primary-dark transition-colors text-sm sm:text-base font-medium"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
            Add Tenant
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
              <input
                type="text"
                placeholder="Search tenants by name, company, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blacklisted">Blacklisted</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 sm:mb-6">
          <p className="text-sm sm:text-base text-gray-600">
            Showing {filteredTenants.length} of {tenants.length} tenants
          </p>
        </div>

        {/* Tenants Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredTenants.map((tenant) => (
            <div key={tenant._id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                    <p className="text-sm text-gray-600">{tenant.companyName}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                  tenant.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {tenant.status}
                </span>
              </div>

              <div className="space-y-2 mb-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{tenant.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>{tenant.phoneNumber}</span>
                </div>
                {tenant.currentShop && (
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <span>Shop {tenant.currentShop.shopNumber} ({tenant.currentShop.floor} Floor)</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Joined: {new Date(tenant.createdAt).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {tenant.rentalHistory.length} rental{tenant.rentalHistory.length !== 1 ? 's' : ''}
                </div>
                <button
                  onClick={() => openTenantModal(tenant)}
                  className="bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary-dark transition-colors text-sm"
                >
                  Manage
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredTenants.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No tenants found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search criteria or filters.</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
              }}
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Tenant Management Modal */}
      {showTenantModal && selectedTenant && (
        <TenantManagementModal
          tenant={selectedTenant}
          onClose={closeTenantModal}
          onTenantUpdate={(tenant) => handleTenantUpdate(tenant as Tenant)}
        />
      )}

      {/* Add Tenant Modal */}
      {showAddTenantModal && (
        <AddTenantModal
          onClose={() => setShowAddTenantModal(false)}
          onTenantCreated={handleTenantCreated}
        />
      )}
    </div>
  );
}
